import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import "./FileConvertor.css";

const FileConvertor = () => {
  // ===== STATE MANAGEMENT =====
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [targetFormat, setTargetFormat] = useState("docx");
  const [conversions, setConversions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // ===== EFFECTS =====
  useEffect(() => {
    if (user) {
      fetchConversions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ===== API CALLS =====
  const fetchConversions = async () => {
    try {
      const response = await api.get("/conversions");
      setConversions(response.data.data.conversions);
    } catch (error) {
      console.error("Error fetching conversions:", error);
    }
  };

  // ===== EVENT HANDLERS =====
  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (!file) return;

    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("targetFormat", targetFormat);

    try {
      await api.post("/conversions/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
      alert("File uploaded successfully! Processing...");
      setFile(null);
      if (user) {
        fetchConversions();
      }
    } catch (error) {
      alert(error.response?.data?.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const fileName = droppedFile.name.toLowerCase();
      if (fileName.endsWith(".pdf") || fileName.endsWith(".docx")) {
        setFile(droppedFile);
      } else {
        alert("Please upload only PDF or DOCX files");
      }
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const removeFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // ===== UTILITY FUNCTIONS =====
  const formatFileSize = (bytes) => {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  // ===== COMPUTED VALUES =====
  const remainingCredits = user?.subscription?.extractionCredits || 0;
  const canConvert = !user || remainingCredits > 0 || !user?.freeTrialUsed;

  // ===== RENDER FUNCTIONS =====
  const renderHeader = () => (
    <div className="text-center mb-5">
      <div className="fcv-header-icon-wrapper d-inline-flex align-items-center justify-content-center mb-4 mt-4">
        <i className="fa fa-refresh fa-3x text-dark"></i>
      </div>
      <h1
        className="display-5 fw-bold mb-3 File Converter
"
      >
        File Converter
      </h1>
      <p
        className="lead mx-auto"
        // style={{ maxWidth: "600px", color: "#dbdbdbff" }}
      >
        Convert your PDF and DOCX files instantly with our powerful online tool.
      </p>
    </div>
  );

  const renderUploadZone = () => (
    <div
      className={`fcv-upload-zone ${isDragging ? "fcv-dragging" : ""} mb-4`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
    >
      <div className="text-center">
        <i className="fa fa-cloud-upload fa-4x text-primary mb-3 fcv-upload-icon"></i>
        <h5 className="fw-bold mb-2">Drag & Drop your file here</h5>
        <p className="text-muted mb-0">or click to browse</p>
        <div className="d-flex align-items-center justify-content-center gap-2 mt-3">
          <span className="badge bg-light text-dark border">
            <i className="fa fa-file-pdf-o me-1"></i>PDF
          </span>
          <span className="badge bg-light text-dark border">
            <i className="fa fa-file-word-o me-1"></i>DOCX
          </span>
        </div>
        <small className="text-muted d-block mt-3">Max file size: 50MB</small>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx"
        onChange={handleFileChange}
        className="d-none"
      />
    </div>
  );

  const renderSelectedFile = () => {
    if (!file) return null;

    return (
      <div className="fcv-file-card alert d-flex align-items-center mb-4 border-0">
        <div className="fcv-file-icon d-flex align-items-center justify-content-center me-3">
          <i className="fa fa-file-text-o fa-lg"></i>
        </div>
        <div className="flex-grow-1">
          <h6 className="mb-1 fw-bold">{file.name}</h6>
          <small className="text-muted">{formatFileSize(file.size)}</small>
        </div>
        <button
          type="button"
          className="btn-close"
          onClick={removeFile}
          aria-label="Remove file"
        ></button>
      </div>
    );
  };

  const renderFormatSelector = () => (
    <div className="row g-3 mb-4">
      <div className="col-md-6">
        <div
          className={`fcv-format-card ${
            targetFormat === "docx" ? "fcv-format-active" : ""
          }`}
          onClick={() => setTargetFormat("docx")}
        >
          <div className="d-flex align-items-center justify-content-center mb-3">
            <i className="fa fa-file-pdf-o fa-3x text-danger me-3"></i>
            <i className="fa fa-long-arrow-right fa-2x text-muted mx-2"></i>
            <i className="fa fa-file-word-o fa-3x text-primary ms-3"></i>
          </div>
          <h6 className="text-center fw-bold mb-0">PDF to DOCX</h6>
        </div>
      </div>
      <div className="col-md-6">
        <div
          className={`fcv-format-card ${
            targetFormat === "pdf" ? "fcv-format-active" : ""
          }`}
          onClick={() => setTargetFormat("pdf")}
        >
          <div className="d-flex align-items-center justify-content-center mb-3">
            <i className="fa fa-file-word-o fa-3x text-primary me-3"></i>
            <i className="fa fa-long-arrow-right fa-2x text-muted mx-2"></i>
            <i className="fa fa-file-pdf-o fa-3x text-danger ms-3"></i>
          </div>
          <h6 className="text-center fw-bold mb-0">DOCX to PDF</h6>
        </div>
      </div>
    </div>
  );

  const renderConversionHistory = () => {
    if (!user) return null;

    return (
      <div className="card border-0 shadow-sm fcv-card mt-4">
        <div className="card-header bg-white border-bottom py-3">
          <h5 className="mb-0 d-flex align-items-center">
            <i className="fa fa-history text-primary me-2"></i>
            Recent Conversions
          </h5>
        </div>
        <div className="card-body p-0">
          {conversions.length === 0 ? (
            <div className="text-center py-5">
              <i className="fa fa-inbox fa-4x text-muted mb-3 d-block"></i>
              <h5 className="text-muted mb-2">No conversions yet</h5>
              <p className="text-muted small">
                Your conversion history will appear here
              </p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="border-0 px-4 py-3">File Name</th>
                    <th className="border-0 px-4 py-3">Conversion</th>
                    <th className="border-0 px-4 py-3">Status</th>
                    <th className="border-0 px-4 py-3">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {conversions.map((conv) => (
                    <tr key={conv._id}>
                      <td className="px-4">
                        <div className="d-flex align-items-center">
                          <i className="fa fa-file-text-o text-muted me-2"></i>
                          <span
                            className="text-truncate"
                            style={{ maxWidth: "200px" }}
                          >
                            {conv.originalFilename}
                          </span>
                        </div>
                      </td>
                      <td className="px-4">
                        <div className="d-flex align-items-center gap-1">
                          <span className="badge bg-light text-dark border">
                            {conv.originalFormat?.toUpperCase()}
                          </span>
                          <i className="fa fa-arrow-right text-muted"></i>
                          <span className="badge bg-light text-dark border">
                            {conv.targetFormat?.toUpperCase()}
                          </span>
                        </div>
                      </td>
                      <td className="px-4">
                        <span
                          className={`fcv-status-badge fcv-status-${conv.status}`}
                        >
                          {conv.status}
                        </span>
                      </td>
                      <td className="px-4">
                        <small className="text-muted">
                          {new Date(conv.createdAt).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "short",
                              day: "numeric",
                            }
                          )}
                        </small>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSidebar = () => {
    if (user) {
      return (
        <div className="card border-0 shadow-sm fcv-card h-100">
          <div className="card-header bg-white border-bottom py-3">
            <h5 className="mb-0 d-flex align-items-center">
              <i className="fa fa-user-circle text-primary me-2"></i>
              Account Summary
            </h5>
          </div>
          <div className="card-body p-4">
            {/* Credits Display */}
            <div className="fcv-credits-display text-center mb-4 p-4 rounded-3">
              <div className="fcv-credits-number display-3 fw-bold mb-2">
                {remainingCredits}
              </div>
              <div className="text-muted fw-semibold">Credits Remaining</div>
            </div>

            {/* Account Stats */}
            <div className="list-group list-group-flush">
              <div className="list-group-item d-flex justify-content-between align-items-center border-0 px-0 py-3">
                <span className="d-flex align-items-center">
                  <i className="fa fa-star text-warning me-2"></i>
                  Plan
                </span>
                <span className="badge bg-primary rounded-pill px-3">
                  {user.subscription?.plan || "Free"}
                </span>
              </div>
              <div className="list-group-item d-flex justify-content-between align-items-center border-0 px-0 py-3">
                <span className="d-flex align-items-center">
                  <i className="fa fa-check-circle text-success me-2"></i>
                  Free Trial
                </span>
                <span
                  className={`badge rounded-pill px-3 ${
                    user.freeTrialUsed ? "bg-secondary" : "bg-success"
                  }`}
                >
                  {user.freeTrialUsed ? "Used" : "Available"}
                </span>
              </div>
            </div>

            {/* Upgrade Button */}
            {user.subscription?.plan === "free" && (
              <div className="mt-4">
                <a
                  href="/billing"
                  className="btn btn-outline-primary w-100 rounded-pill"
                >
                  <i className="fa fa-rocket me-2"></i>
                  Upgrade Plan
                </a>
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="card border-0 shadow-sm fcv-card">
        <div className="card-header bg-white border-bottom py-3">
          <h5 className="mb-0 d-flex align-items-center">
            <i className="fa fa-gift text-primary me-2"></i>
            Create an Account
          </h5>
        </div>
        <div className="card-body p-4">
          <p className="text-muted mb-4">
            Sign up to unlock additional features:
          </p>

          <div className="mb-4">
            {[
              { icon: "check-circle", text: "Track conversion history" },
              { icon: "folder-open", text: "Manage your files" },
              { icon: "usd", text: "Upgrade to Pro plan" },
              { icon: "battery-three-quarters", text: "Get monthly credits" },
              { icon: "life-ring", text: "Priority support" },
            ].map((feature, index) => (
              <div key={index} className="d-flex align-items-center mb-3">
                <i className={`fa fa-${feature.icon} text-success me-3`}></i>
                <span>{feature.text}</span>
              </div>
            ))}
          </div>

          <a
            href="/register"
            className="btn btn-primary w-100 rounded-pill mb-2"
          >
            <i className="fa fa-rocket me-2"></i>
            Sign Up Free
          </a>
          <a
            href="/login"
            className="btn btn-outline-primary w-100 rounded-pill"
          >
            Already have an account?
          </a>
        </div>
      </div>
    );
  };

  // ===== MAIN RENDER =====
  return (
    <div className="fcv-wrapper">
      <div className="container py-5">
        {renderHeader()}

        <div className="row g-4">
          {/* Main Content */}
          <div className="col-lg-8">
            {/* Upload Card */}
            <div className="card border-0 shadow-sm fcv-card">
              <div className="card-header bg-white border-bottom py-3">
                <h5 className="mb-0 d-flex align-items-center">
                  <i className="fa fa-cloud-upload text-primary me-2"></i>
                  Upload & Convert
                </h5>
              </div>
              <div className="card-body p-4">
                {/* Guest Mode Alert */}
                {!user && (
                  <div className="alert alert-info fcv-alert d-flex align-items-start mb-4">
                    <i className="fa fa-info-circle fa-lg me-3 mt-1"></i>
                    <div className="flex-grow-1">
                      <strong className="d-block mb-1">Guest Mode</strong>
                      <p className="mb-0 small">
                        Test file conversion without an account.{" "}
                        <a href="/register" className="alert-link fw-bold">
                          Sign up
                        </a>{" "}
                        to track conversions and unlock more features.
                      </p>
                    </div>
                  </div>
                )}

                {/* No Credits Alert */}
                {user && !canConvert && (
                  <div className="alert alert-warning fcv-alert mb-4">
                    <div className="d-flex align-items-start">
                      <i className="fa fa-exclamation-triangle fa-lg me-3 mt-1"></i>
                      <div className="flex-grow-1">
                        <h6 className="alert-heading mb-2">
                          No extraction credits remaining
                        </h6>
                        <p className="mb-3 small">
                          Please upgrade your plan to continue converting files.
                        </p>
                        <a
                          href="/billing"
                          className="btn btn-warning btn-sm rounded-pill"
                        >
                          <i className="fa fa-rocket me-2"></i>
                          Upgrade Plan
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {canConvert && (
                  <form onSubmit={handleFileUpload}>
                    {renderUploadZone()}
                    {renderSelectedFile()}
                    {renderFormatSelector()}

                    {/* Convert Button */}
                    <button
                      type="submit"
                      className="btn btn-primary btn-lg w-100 py-3 rounded-pill fcv-convert-btn"
                      disabled={loading || !file}
                    >
                      {loading ? (
                        <>
                          <i className="fa fa-spinner fa-spin me-2"></i>
                          Converting...
                        </>
                      ) : (
                        <>
                          <i className="fa fa-refresh me-2"></i>
                          Convert File
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>

            {/* Conversion History */}
            {renderConversionHistory()}
          </div>

          {/* Sidebar */}
          <div className="col-lg-4">{renderSidebar()}</div>
        </div>
      </div>
    </div>
  );
};

export default FileConvertor;
